/**
 * Debug Error Boundary - Enhanced error boundary with detailed reporting
 */

import React, { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: (error: Error, errorInfo: ErrorInfo) => ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorId: string;
}

export class DebugErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: `error_${Date.now()}`
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
      errorId: `error_${Date.now()}`
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({
      error,
      errorInfo
    });

    // Enhanced error reporting
    this.reportError(error, errorInfo);
  }

  private reportError = (error: Error, errorInfo: ErrorInfo) => {
    const errorReport = {
      timestamp: new Date().toISOString(),
      errorId: this.state.errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      props: this.props,
      url: window.location.href,
      userAgent: navigator.userAgent,
      memory: (performance as any).memory ? {
        usedJSHeapSize: (performance as any).memory.usedJSHeapSize,
        totalJSHeapSize: (performance as any).memory.totalJSHeapSize,
        jsHeapSizeLimit: (performance as any).memory.jsHeapSizeLimit
      } : undefined,
      viewport: {
        width: window.innerWidth,
        height: window.innerHeight
      },
      // Capture current debug state if available
      debugState: this.captureDebugState()
    };

    // Log to console for immediate debugging
    console.group(`🚨 Error Boundary Triggered - ${this.state.errorId}`);
    console.error('Error:', error);
    console.error('Error Info:', errorInfo);
    console.error('Full Report:', errorReport);
    console.groupEnd();

    // Store in localStorage for persistence
    try {
      const existingErrors = JSON.parse(localStorage.getItem('syntaxlens_errors') || '[]');
      existingErrors.push(errorReport);
      // Keep only last 10 errors
      localStorage.setItem('syntaxlens_errors', JSON.stringify(existingErrors.slice(-10)));
    } catch (e) {
      console.warn('Could not save error to localStorage:', e);
    }

    // Send to monitoring service (if available)
    if (window.location.hostname !== 'localhost') {
      this.sendToMonitoring(errorReport);
    }
  };

  private captureDebugState = () => {
    try {
      return {
        directSelections: (window as any).__directSelections,
        debugSteps: (window as any).__debugSteps,
        zenPhase: (window as any).__zenPhase,
        lastError: (window as any).__lastError
      };
    } catch (e) {
      return { captureError: e.message };
    }
  };

  private sendToMonitoring = async (errorReport: any) => {
    try {
      // This would typically send to your error monitoring service
      // For now, we'll just log it
      console.info('Error report prepared for monitoring:', errorReport);
    } catch (e) {
      console.warn('Could not send error to monitoring service:', e);
    }
  };

  private handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: `error_${Date.now()}`
    });
  };

  private exportErrorReport = () => {
    const { error, errorInfo, errorId } = this.state;
    if (!error || !errorInfo) return;

    const errorReport = {
      timestamp: new Date().toISOString(),
      errorId,
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      debugState: this.captureDebugState(),
      url: window.location.href,
      userAgent: navigator.userAgent
    };

    const dataStr = JSON.stringify(errorReport, null, 2);
    const dataUri = 'data:application/json;charset=utf-8,' + encodeURIComponent(dataStr);
    const exportFileDefaultName = `syntaxlens-error-${errorId}.json`;

    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportFileDefaultName);
    linkElement.click();
  };

  render() {
    if (this.state.hasError) {
      const { error, errorInfo, errorId } = this.state;
      
      // Use custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback(error!, errorInfo!);
      }

      // Default error UI
      return (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, #0a0e1a 0%, #1a1f2e 100%)',
          color: '#e2e8f0',
          fontFamily: 'Inter, system-ui, sans-serif',
          zIndex: 10000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '24px'
        }}>
          <div style={{
            maxWidth: '600px',
            width: '100%',
            background: '#232938',
            border: '1px solid #2d3748',
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.5)'
          }}>
            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #ef4444, #dc2626)',
              padding: '20px',
              textAlign: 'center'
            }}>
              <h1 style={{
                margin: '0 0 8px 0',
                fontSize: '24px',
                fontWeight: '600'
              }}>
                🚨 SyntaxLens Error
              </h1>
              <p style={{
                margin: 0,
                fontSize: '14px',
                opacity: 0.9
              }}>
                Error ID: {errorId}
              </p>
            </div>

            {/* Error Details */}
            <div style={{ padding: '24px' }}>
              <div style={{
                background: '#1a1f2e',
                border: '1px solid #2d3748',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '20px'
              }}>
                <h3 style={{
                  margin: '0 0 12px 0',
                  fontSize: '16px',
                  color: '#4299e1'
                }}>
                  Error Message
                </h3>
                <p style={{
                  margin: 0,
                  fontSize: '14px',
                  fontFamily: 'monospace',
                  color: '#ef4444'
                }}>
                  {error?.message}
                </p>
              </div>

              {/* Stack Trace */}
              <details style={{ marginBottom: '20px' }}>
                <summary style={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  🔍 Stack Trace
                </summary>
                <pre style={{
                  background: '#0a0e1a',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  overflow: 'auto',
                  maxHeight: '200px',
                  margin: 0,
                  border: '1px solid #2d3748'
                }}>
                  {error?.stack}
                </pre>
              </details>

              {/* Component Stack */}
              <details style={{ marginBottom: '20px' }}>
                <summary style={{
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  marginBottom: '8px'
                }}>
                  📦 Component Stack
                </summary>
                <pre style={{
                  background: '#0a0e1a',
                  padding: '12px',
                  borderRadius: '6px',
                  fontSize: '12px',
                  overflow: 'auto',
                  maxHeight: '200px',
                  margin: 0,
                  border: '1px solid #2d3748'
                }}>
                  {errorInfo?.componentStack}
                </pre>
              </details>

              {/* Action Buttons */}
              <div style={{
                display: 'flex',
                gap: '12px',
                flexWrap: 'wrap'
              }}>
                <button
                  onClick={this.handleRetry}
                  style={{
                    background: 'linear-gradient(135deg, #4299e1, #63b3ed)',
                    color: 'white',
                    border: 'none',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'transform 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    (e.target as HTMLButtonElement).style.transform = 'translateY(-1px)';
                  }}
                  onMouseOut={(e) => {
                    (e.target as HTMLButtonElement).style.transform = 'translateY(0)';
                  }}
                >
                  🔄 Try Again
                </button>

                <button
                  onClick={this.exportErrorReport}
                  style={{
                    background: '#2d3748',
                    color: '#e2e8f0',
                    border: '1px solid #4a5568',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    (e.target as HTMLButtonElement).style.background = '#4a5568';
                  }}
                  onMouseOut={(e) => {
                    (e.target as HTMLButtonElement).style.background = '#2d3748';
                  }}
                >
                  📊 Export Report
                </button>

                <button
                  onClick={() => window.location.reload()}
                  style={{
                    background: '#2d3748',
                    color: '#e2e8f0',
                    border: '1px solid #4a5568',
                    padding: '12px 20px',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                    transition: 'background-color 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    (e.target as HTMLButtonElement).style.background = '#4a5568';
                  }}
                  onMouseOut={(e) => {
                    (e.target as HTMLButtonElement).style.background = '#2d3748';
                  }}
                >
                  🔄 Reload Page
                </button>
              </div>

              {/* Helpful Tips */}
              <div style={{
                marginTop: '20px',
                padding: '16px',
                background: 'rgba(66, 153, 225, 0.1)',
                border: '1px solid rgba(66, 153, 225, 0.2)',
                borderRadius: '8px'
              }}>
                <h4 style={{
                  margin: '0 0 8px 0',
                  fontSize: '14px',
                  color: '#4299e1'
                }}>
                  💡 Debugging Tips
                </h4>
                <ul style={{
                  margin: 0,
                  paddingLeft: '20px',
                  fontSize: '13px',
                  lineHeight: '1.5'
                }}>
                  <li>Try refreshing the page</li>
                  <li>Check browser console for additional errors</li>
                  <li>Clear browser cache and cookies</li>
                  <li>Export the error report for detailed analysis</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}